import React, { useEffect, useState } from 'react';
import {
  ToastContainer,
  Header,
  Footer,
  ErrorContainer,
  BlockLoader,
  NotificationManager,
  IEBanner,
  HEADER_HEIGHT,
  Loader,
  Maintenance,
  LeadManager,
} from '@sky-tv-group/components';
import { myAccountSWR } from './swr';
import { getUser, useAuth0 } from './auth0/store';
import { routes } from './routes';
import { Route, Switch, Redirect } from 'react-router-dom';
import {
  pageErrorMessage,
  productStoreApi,
  T_Product,
  useCustomerStore,
  useOrderStore,
  useProductStore,
  orderStoreApi,
  useBroadband,
  getTuiAddressCode,
  couponStoreApi,
  useManagePackage,
  useSplitIO,
  SPLITIO_KEY,
  useAnalytics,
  CreditCampaignCodes,
  productSkuIds,
  BACKEND_ENDPOINT,
  useCouponStore,
  // SPAs,
  // PACKAGE_TYPE,
} from '@sky-tv-group/shared';

import { kkService, prequalService } from './services';
import { DOMAIN, ACCOUNT_URL } from './config';
import { TotalHeader } from './components/total-header';
import { SubMenu } from './components/SubMenu';
import { T_Profile } from '@sky-tv-group/shared/src/types';
import { set, clearSessionStorageState, useStore } from './store';
import { useLocation } from 'react-router-dom';
import { AddressBar } from './components/AddressBar';
import { AppRoutes } from './constants';
import { v4 as uuidv4 } from 'uuid';
interface Props {
  profile: T_Profile | undefined;
  packages: T_Product[] | undefined;
  isAuthenticated: boolean;
  isSsnCustomer?: boolean;
}

let Body = ({ profile, packages, isAuthenticated, isSsnCustomer }: Props) => {
  const [BBCreditOfferFlag] = useSplitIO(SPLITIO_KEY.SKYWEB_BB_CREDIT_OFFER);

  const [errorPresent, setErrorPresent] = useState(false);
  const [productsUpdated, setProductsUpdated] = useState(false);
  const { data: billing }: any = myAccountSWR.useBilling();
  const { customerId, getTempCustomerId, getCustomerId, setExistingCustomerId } = useCustomerStore(s =>
    // Selecting parts of store to bind to root component
    ({
      customerId: s.kk,
      getTempCustomerId: s.getTempCustomerId,
      getCustomerId: s.getCustomerIdFromProfile,
      setExistingCustomerId: s.setExistingCustomerId,
    })
  );
  const location = useLocation();
  const showAddressBarInHeader = location.pathname === AppRoutes.Home;

  const {
    loadProductStore,
    initializedProductStore,
    updateFromOrderProducts,
    updateBillingProductsFromOrderProducts,
    setProductStoreUpgrade,
  } = useProductStore();

  const { loadOrderStore, updateOrder, order } = useOrderStore();

  const updateFromOrderProductsCall = (authenticated: boolean) => {
    const orderProducts = orderStoreApi.getState().order?.orderProducts;
    // order with a coupon will have things that wont be in query2 (this is for no existing campaign).
    // Need to toggle those products
    if (orderProducts && !authenticated) {
      updateFromOrderProducts(orderProducts);
    } else if (packages && authenticated) {
      updateBillingProductsFromOrderProducts(packages, orderProducts ?? []);
    }
  };

  const { isCustomerNeedsAccountUpdate } = myAccountSWR.usePackages();

  useEffect(() => {
    const updateOrderAtStart = async (customerId: string) => {
      try {
        if (isAuthenticated && profile && (!customerId || customerId === '')) {
          // If current user is authenticated but customerId is not in storage (maybe redirected from another domain),
          // wait for his profile to load so we can properly load his existing order
          customerId = getCustomerId(profile);
          setExistingCustomerId(customerId);
        } else if (!isAuthenticated && (!customerId || customerId === '')) {
          // Customer is guest and new
          customerId = await getTempCustomerId(kkService);
        } else if (isAuthenticated && profile && (customerId || customerId !== '')) {
          // Customer is logged in, has profile and customerId in storage.
          const profileCustomerId = getCustomerId(profile);
          // If customerId in storage differs from profileCustomerId
          // Do steps:
          //  1. Retrieve the customerId's order from KK so we get his existing product orders
          //  2. Update order store and product store quantity from the response of step 1
          //  3. Set profileCustomerId as the new customerId
          //  4. Update KK order using his profileCustomerId
          if (profileCustomerId !== customerId) {
            await loadProductStore(kkService, customerId, false, billing);

            updateFromOrderProductsCall(isAuthenticated);

            customerId = profileCustomerId;
            setExistingCustomerId(profileCustomerId);

            // If logged in Customer is Vodafone or Igloo customer do not add products
            if (!isCustomerNeedsAccountUpdate) {
              await updateOrder(
                kkService,
                profileCustomerId,
                productStoreApi.getState().getBasketItemsToAddToOrder(),
                couponStoreApi.getState().coupons
              );
            }
          }
        }

        if (customerId && customerId !== '') {
          await loadOrderStore(kkService, customerId);
          await loadProductStore(kkService, customerId, false, billing);

          updateFromOrderProductsCall(isAuthenticated);

          // If logged in Customer is Vodafone or Igloo customer do not add products
          if (!isCustomerNeedsAccountUpdate) {
            await updateOrder(
              kkService,
              customerId,
              productStoreApi.getState().getBasketItemsToAddToOrder(),
              couponStoreApi.getState().coupons
            );
          }
          setProductsUpdated(true);
        }
      } catch (errors) {
        setErrorPresent(true);
      }
    };
    updateOrderAtStart(customerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  let fibrePro = order?.orderProducts.find(p => p.product.sku === productSkuIds.broadbandLightningFastWiFi.primary);
  let fibreProBoost = order?.orderProducts.find(
    p => p.product.sku === productSkuIds.broadbandLightningFastWiFiBoost.primary
  );

  let fibreEveryday = order?.orderProducts.find(p => p.product.sku === productSkuIds.broadbandWifi100.primary);
  let fibreEverydayBoost = order?.orderProducts.find(
    p => p.product.sku === productSkuIds.broadbandWifi100Boost.primary
  );

  let fibreStarter = order?.orderProducts.find(p => p.product.sku === productSkuIds.broadbandEssnFibre.primary);
  let fibreStarterBoost = order?.orderProducts.find(p => p.product.sku === productSkuIds.broadbandEssnFibBoost.primary);
  const { voucher } = useCouponStore();

  useEffect(() => {
    const loadCoupons = async () => {
      if (!BBCreditOfferFlag) return;

      let coupons = Object.values(CreditCampaignCodes.BROADBAND);
      coupons.forEach(coupon => {
        couponStoreApi.getState().clearCouponFromCode(coupon);
      });

      if (!voucher?.voucherCode) {
        if (fibrePro?.quantity! > 0 || fibreProBoost?.quantity! > 0) {
          const coupon = await kkService.getCoupon(CreditCampaignCodes.BROADBAND.CREDIT_BB_ONLY);
          if (window.location.pathname != AppRoutes.Home && window.location.pathname != AppRoutes.Broadband) {
            if (coupon) couponStoreApi.getState().applyCouponToStore(coupon);
          }
        }
        if (fibreEveryday?.quantity! > 0 || fibreEverydayBoost?.quantity! > 0) {
          const coupon = await kkService.getCoupon(CreditCampaignCodes.BROADBAND.CREDIT_BB_ONLY);
          if (window.location.pathname != AppRoutes.Home && window.location.pathname != AppRoutes.Broadband) {
            if (coupon) couponStoreApi.getState().applyCouponToStore(coupon);
          }
        }
        if (fibreStarter?.quantity! > 0 || fibreStarterBoost?.quantity! > 0) {
          const coupon = await kkService.getCoupon(CreditCampaignCodes.BROADBAND.CREDIT_BB_ONLY);
          if (window.location.pathname != AppRoutes.Home && window.location.pathname != AppRoutes.Broadband) {
            if (coupon) couponStoreApi.getState().applyCouponToStore(coupon);
          }
        }
      }

      if (
        (fibreEveryday || fibrePro || fibreEverydayBoost || fibreProBoost || fibreStarter || fibreStarterBoost) &&
        window.location.pathname === AppRoutes.OrderSummary
      ) {
        await updateOrder(
          kkService,
          customerId,
          productStoreApi.getState().getBasketItemsToAddToOrder(),
          couponStoreApi.getState().coupons
        );
      }
      updateFromOrderProducts(orderStoreApi.getState().order?.orderProducts ?? []);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    };
    loadCoupons();
  }, [
    fibrePro?.quantity,
    fibreProBoost?.quantity,
    fibreEveryday?.quantity,
    fibreEverydayBoost?.quantity,
    fibreStarter?.quantity,
    fibreStarterBoost?.quantity,
    window.location.pathname === AppRoutes.OrderSummary,
  ]);

  useEffect(() => {
    // Tell analytics whether it's upgrade or acquisition.
    setProductStoreUpgrade(isAuthenticated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateFromOrderProductsCall(isAuthenticated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, isAuthenticated]);

  const navLinks = useAuth0(s => s.navLinks);

  // only true if we catch an error on load - for konakart
  if (errorPresent)
    return (
      <>
        <ErrorContainer
          headerText={pageErrorMessage.header}
          lineOne={pageErrorMessage.lineOne}
          lineTwo={pageErrorMessage.lineTwo}
        />
      </>
    );

  if (!(initializedProductStore && productsUpdated) && isAuthenticated)
    return (
      <div className="h-128">
        <BlockLoader />
      </div>
    );

  return (
    <div className="flex flex-col">
      <div className="sticky z-40 w-full left-0 overflow-x-auto shadow-md" style={{ top: HEADER_HEIGHT }}>
        {showAddressBarInHeader && <AddressBar />}
        <TotalHeader />
      </div>
      <div className="flex-1">
        <div>
          {!isSsnCustomer && <NotificationManager notificationsEndpoint={BACKEND_ENDPOINT} domain={DOMAIN} />}
          <Switch>
            {routes.map(({ path, exact, component }) => (
              <Route key={path} exact={exact} path={path} component={component} />
            ))}
            <Route path="*">
              <Redirect to="/notfound" />
            </Route>
          </Switch>
        </div>
        {!isSsnCustomer && <LeadManager apiEndpoint={BACKEND_ENDPOINT} isSignedIn={isAuthenticated} />}
        <Footer links={navLinks} />
        <ToastContainer />
      </div>
    </div>
  );
};

const App = () => {
  const [isMaintenanceMode] = useSplitIO(SPLITIO_KEY.MAINTENANCE_MODE_BROADBAND);
  const [isSurvicateEanbled] = useSplitIO(SPLITIO_KEY.ENABLE_SURVICATE);
  const [isQualtricsSurvicateEanbled] = useSplitIO(SPLITIO_KEY.SKYWEB_ENABLE_QUALTRICS);
  const { data: profile } = myAccountSWR.useProfile();
  const { data: packages, hasActiveOrPendingBroadbandPackage, summerSetAccount } = myAccountSWR.usePackages();
  const { data: customer } = myAccountSWR.useCustomer();
  const { data: billing } = myAccountSWR.useBilling();
  const { isAuthenticated, loginWithRedirect, login, logout, navLinks } = useAuth0();
  const { identify, page } = useAnalytics();
  const value = isAuthenticated ? false : new URLSearchParams(window.location.search).get('ssn');
  const isSsnCustomer = value === 'true';

  useEffect(() => {
    if (isAuthenticated) {
      if (profile) {
        // Analytics
        const user = getUser();
        const personalDetails = customer?.Parties?.[0].types['MP:Primary']?.personalDetails;
        const contactDetails = customer?.Parties?.[0].types['MP:Primary']?.contactDetails?.[0];
        // eslint-disable-next-line no-useless-concat
        const userPhone = (contactDetails?.areaCode ?? '' + contactDetails?.number ?? '' + '').toString();

        const identifyPayload = {
          sub: user.sub,
          email: profile.email,
          userPhone: userPhone,
          accountNumber: profile.accountNumber,
          profileId: profile.profileId,
          firstName: personalDetails?.firstName ?? '',
          lastName: personalDetails?.lastName ?? '',
          dob: personalDetails?.dob ?? '',
          customerGroupTypesString: profile?.groups.join(','),
        };

        identify(user.sub, profile.email, profile.accountNumber, profile.profileId, identifyPayload);
        setTimeout(() => {
          page();
        }, 1000);
      }
    } else {
      page();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, profile]);

  useEffect(() => {
    if (summerSetAccount && isAuthenticated) {
      window.location.assign(ACCOUNT_URL);
    }
  }, [summerSetAccount, isAuthenticated]);

  // ids straight from your Qualtrics snippet
  const QUALTRICS_TOKEN = process.env.REACT_APP_QUALTRICS_ID!;
  const QUALTRICS_DIV_ID = `ZN_${QUALTRICS_TOKEN}`;
  const QUALTRICS_SCRIPT_ID = 'qualtrics-siteintercept';
  const QUALTRICS_SRC = `https://zn${QUALTRICS_TOKEN?.toLowerCase()}-skyexperience.siteintercept.qualtrics.com/SIE/?Q_ZID=${QUALTRICS_DIV_ID}`;

  useEffect(() => {
    // rename your flag if you like; keeping your original here for minimal change
    const enabled = isQualtricsSurvicateEanbled;
    if (typeof window === 'undefined') return; // SSR guard

    const ensureContainer = () => {
      let div = document.getElementById(QUALTRICS_DIV_ID);
      if (!div) {
        div = document.createElement('div');
        div.id = QUALTRICS_DIV_ID;
        // (optional) makes it easier to find in DOM
        div.setAttribute('data-qa', 'qualtrics-container');
        // Qualtrics expects it in the body
        document.body.appendChild(div);
      }
      return div;
    };

    const addScript = () => {
      if (document.getElementById(QUALTRICS_SCRIPT_ID)) return; // already added
      const s = document.createElement('script');
      s.id = QUALTRICS_SCRIPT_ID;
      s.type = 'text/javascript';
      s.async = true;
      s.src = QUALTRICS_SRC;
      document.body.appendChild(s);
    };

    const removeScriptAndContainer = () => {
      document.getElementById(QUALTRICS_SCRIPT_ID)?.remove();
      document.getElementById(QUALTRICS_DIV_ID)?.remove();
    };

    if (enabled) {
      ensureContainer();
      // Qualtrics snippet waits for window load; replicate that logic
      const onLoad = () => addScript();
      if (document.readyState !== 'complete') {
        window.addEventListener('load', onLoad, { once: true });
        // cleanup this listener if we toggle off before load fires
        return () => window.removeEventListener('load', onLoad);
      } else {
        addScript();
      }
    } else {
      removeScriptAndContainer();
    }

    // Also clean up on unmount if the toggle is off; if on, you may want to keep it.
    return () => {
      if (!enabled) removeScriptAndContainer();
    };
  }, [isQualtricsSurvicateEanbled]);

  useEffect(() => {
    const eleId = 'survicate';
    if (isSurvicateEanbled) {
      (function(w) {
        var s = document.createElement('script');
        s.id = eleId;
        s.src = 'https://survey.survicate.com/workspaces/e78f97f8d7583351bf5dd2cd9cd4393f/web_surveys.js';
        s.async = true;
        var e = document.getElementsByTagName('script')[0];
        e.parentNode!.insertBefore(s, e);
      })(window);
    } else {
      document.getElementById(eleId)?.remove();
    }
  }, [isSurvicateEanbled]);

  const { getPrequalData } = useBroadband(prequalService);
  const { persistToStorage, clearStore, clearPersistedButtonStates, isBundle } = useStore(s => ({
    persistToStorage: s.persistToStorage,
    clearPersistedButtonStates: s.clearPersistedButtonStates,
    clearStore: s.clearStore,
    isBundle: s.isBundle,
  }));
  const { setExistingCustomerId, customerId, sessionId, setSessionId } = useCustomerStore(s => ({
    setExistingCustomerId: s.setExistingCustomerId,
    customerId: s.kk,
    sessionId: s.sessionId,
    setSessionId: s.setSessionId,
  }));

  const { orderProductsAddedToCart } = useManagePackage(myAccountSWR);

  const loginCallbackUrls: string[] = [AppRoutes.Home, AppRoutes.Broadband].map(r => `${window.location.origin}${r}`);

  const queryParams = useLocation().search;

  const loginHandler = () => {
    const allowLogin = loginCallbackUrls.includes(window.location.href);
    // only / and /broadband route is allowed for login callback redirects, everything else route to home.
    // this is because we don't allow existing customers from logging-in in acquisition urls
    // like '/checkout' and '/tv', it messes up their order
    //
    // also if a bundle plan is selected, we redirect and clear cart
    if (!allowLogin || isBundle) {
      // clear cart using orderStoreApi to also clear the order store (kkService.updateOrder doesn't)
      orderStoreApi.getState().clearOrder(kkService, customerId);
      login();
    } else {
      loginWithRedirect(window.location.href);
    }
  };

  const logoutHandler = async () => {
    clearPersistedButtonStates();
    clearSessionStorageState();
    await kkService.updateOrder(customerId, []);
    // clear id on logout so guests will use a different id
    setExistingCustomerId('');
    logout();
  };

  async function setCustomerPrequal() {
    if (customer) {
      //Clear guest prequal and selected address when you login
      clearStore();

      let tui = getTuiAddressCode(customer);
      let prequal = await getPrequalData(tui);
      // NOTE: I'm using the 'set' method from store to avoid re-rendering this Home component when store state changes
      //Set selectedAddressId to empty on login
      set({ authTuiAddress: tui, selectedAddressId: '', customer: customer });
      // if prequal is present, else show toast maybe?
      if (prequal) {
        set({ authenticatedPrequalData: prequal, useGuestPrequal: false });
      }
      set({ isLoadingPrequal: false });
      persistToStorage();
    }
  }

  useEffect(() => {
    // If there's no session Id and there are new products in the cart and user is logged in, it means that this was abandoned.
    if (!sessionId && isAuthenticated && orderProductsAddedToCart.length > 0) {
      // Commenting out this event for now.  Will revisit later.
      // returnToAbandonedCart(
      //   orderProductsAddedToCart,
      //   true,
      //   SPAs.BROADBAND,
      //   isBundle ? PACKAGE_TYPE.DTH_BROADBAND : PACKAGE_TYPE.BROADBAND
      // );
      //console.log("%cUSER JUST LOGGED IN!!!!!", "background-color: yellow")

      // After you've triggered an abandoned cart event, generate a new session for this journey.
      if (!sessionId) {
        setSessionId(uuidv4());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderProductsAddedToCart.length > 0, isAuthenticated, sessionId]);

  // Updating prequal if user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      set({ isLoadingPrequal: true });
      setCustomerPrequal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    set({ hasActiveOrPendingBroadbandPackage });
  }, [hasActiveOrPendingBroadbandPackage]);

  useEffect(() => {
    // Get the session ID from the URL if it exists.
    const sessionIdParam = queryParams ? new URLSearchParams(queryParams).get('sid') : '';
    if (sessionIdParam) {
      setSessionId(sessionIdParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if customer is authenticated,
  // wait for his profile data and billing to be fully loaded before rendering the body
  if (isAuthenticated && (!profile || !billing || !customer)) return <Loader bgOpacity={false} />;

  return (
    <div>
      {/* <Helmet>
        <link rel="canonical" href={window.location.protocol + '//' + window.location.host} />
      </Helmet> */}
      <Header
        isSignedIn={isAuthenticated}
        name={profile?.displayName || profile?.firstName}
        SessionClickHandler={isAuthenticated ? logoutHandler : loginHandler}
        links={navLinks}
      />
      {!isSsnCustomer && <SubMenu />}
      {isMaintenanceMode ? (
        <Maintenance />
      ) : (
        <>
          <IEBanner>
            <Body
              profile={profile}
              packages={packages}
              isAuthenticated={isAuthenticated}
              isSsnCustomer={isSsnCustomer}
            />
          </IEBanner>
        </>
      )}
    </div>
  );
};

export { App };
